/* ============================================================================
   Myoform - "What We Test" genetic insight directory
   ----------------------------------------------------------------------------
   Renders the whole directory from window.MyoformWhatWeTest (see
   assets/dna-what-we-test-data.js). Nothing in this file needs touching to
   add an insight or a category.

   Responsibilities:
     - build the category nav and its sliding indicator
     - filter on search + category, render the grid, keep the count in sync
     - open / close the detail drawer (ESC, backdrop, close button, focus)
     - keep the URL hash in step so an insight can be linked directly
   ========================================================================== */

(function () {
  'use strict';

  var ALL = 'all';

  /* ── helpers ───────────────────────────────────────────────────────────── */

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* Wraps every occurrence of the query in <mark>. Runs on already-escaped
     text, so the needle is escaped the same way before matching. */
  function highlight(escapedText, query) {
    if (!query) return escapedText;
    var needle = escapeRegExp(escapeHtml(query));
    return escapedText.replace(new RegExp('(' + needle + ')', 'gi'), '<mark class="wwt__mark">$1</mark>');
  }

  function icon(name) {
    var id = '#wwt-icon-' + escapeHtml(name);
    return (
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" focusable="false">' +
      '<use href="' + id + '" xlink:href="' + id + '"></use></svg>'
    );
  }

  var ARROW_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M5 12h13M12 5.5 18.5 12 12 18.5"/></svg>';

  /* ── directory ─────────────────────────────────────────────────────────── */

  function WhatWeTest(root) {
    var data = window.MyoformWhatWeTest;
    if (!data || !Array.isArray(data.insights)) return;

    this.root = root;
    this.categories = Array.isArray(data.categories) ? data.categories : [];
    this.insights = data.insights.slice();

    this.state = { category: ALL, query: '' };

    // Label shown instead of a real count while nothing is filtered, e.g.
    // "250+ insights". Blank falls back to the true number of insights.
    this.allLabel = root.dataset.allLabel || '';

    this.el = {
      search: root.querySelector('[data-wwt-search]'),
      clear: root.querySelector('[data-wwt-clear]'),
      popular: root.querySelector('[data-wwt-popular]'),
      navTrack: root.querySelector('[data-wwt-nav-track]'),
      indicator: root.querySelector('[data-wwt-nav-indicator]'),
      resultsBar: root.querySelector('[data-wwt-results]'),
      count: root.querySelector('[data-wwt-count]'),
      catHeader: root.querySelector('[data-wwt-cat-header]'),
      catTitle: root.querySelector('[data-wwt-cat-title]'),
      catDesc: root.querySelector('[data-wwt-cat-desc]'),
      directory: root.querySelector('[data-wwt-directory]'),
      results: root.querySelector('[data-wwt-grid-wrap]'),
      empty: root.querySelector('[data-wwt-empty]'),
      drawer: root.querySelector('[data-wwt-drawer]'),
    };

    this.byId = {};
    this.catById = {};

    var i;
    for (i = 0; i < this.categories.length; i++) {
      this.catById[this.categories[i].id] = this.categories[i];
    }

    /* Pre-compute one lowercase haystack per insight so keystroke filtering
       never re-reads or re-cases the source strings. */
    for (i = 0; i < this.insights.length; i++) {
      var item = this.insights[i];
      var cat = this.catById[item.category];
      this.byId[item.id] = item;
      item._catLabel = cat ? cat.label : item.category;
      item._catIcon = cat ? cat.icon : 'nutrients';
      item._search = [
        item.name,
        item._catLabel,
        item.shortDescription,
        item.keywords || '',
      ]
        .join(' ')
        .toLowerCase();
      item._sort = String(item.name).toLowerCase();
    }

    this.buildNav();
    this.bind();
    this.setupDrawer();
    this.render();

    root.classList.add('is-ready');
    this.openFromHash();
  }

  /* ── category nav ──────────────────────────────────────────────────────── */

  WhatWeTest.prototype.buildNav = function () {
    var track = this.el.navTrack;
    if (!track) return;

    var html = '<button type="button" class="wwt__nav-pill" data-wwt-cat="all" aria-pressed="true">All</button>';

    for (var i = 0; i < this.categories.length; i++) {
      var cat = this.categories[i];
      html +=
        '<button type="button" class="wwt__nav-pill" data-wwt-cat="' +
        escapeHtml(cat.id) +
        '" aria-pressed="false">' +
        icon(cat.icon) +
        '<span>' +
        escapeHtml(cat.label) +
        '</span></button>';
    }

    // The indicator is a sibling of the pills, so it is re-inserted rather
    // than overwritten by the innerHTML above.
    track.insertAdjacentHTML('afterbegin', html);
    this.pills = track.querySelectorAll('[data-wwt-cat]');
  };

  /* Moves the purple pill onto the active button. Reads offsetTop as well as
     offsetLeft because the nav wraps to a second row on narrow desktops. */
  WhatWeTest.prototype.moveIndicator = function () {
    var indicator = this.el.indicator;
    if (!indicator || !this.pills) return;

    var active = null;
    for (var i = 0; i < this.pills.length; i++) {
      if (this.pills[i].dataset.wwtCat === this.state.category) active = this.pills[i];
    }
    if (!active) return;

    indicator.style.width = active.offsetWidth + 'px';
    indicator.style.height = active.offsetHeight + 'px';
    indicator.style.transform = 'translate(' + active.offsetLeft + 'px, ' + active.offsetTop + 'px)';
    indicator.classList.add('is-ready');
  };

  /* ── events ────────────────────────────────────────────────────────────── */

  WhatWeTest.prototype.bind = function () {
    var self = this;

    if (this.el.search) {
      this.el.search.addEventListener('input', function () {
        self.state.query = this.value.trim();
        /* Search is global: typing leaves the category browse state so
           "caffeine" still finds both caffeine insights from any pill. */
        if (self.state.query) self.state.category = ALL;
        self.root.classList.toggle('is-searching', this.value.length > 0);
        self.render();
      });

      // Enter would submit if this ever sits inside a form.
      this.el.search.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') event.preventDefault();
        if (event.key === 'Escape' && this.value) {
          event.stopPropagation();
          self.resetSearch();
        }
      });
    }

    if (this.el.clear) {
      this.el.clear.addEventListener('click', function () {
        self.resetSearch();
      });
    }

    if (this.el.popular) {
      this.el.popular.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-wwt-term]');
        if (!btn) return;
        self.setSearch(btn.dataset.wwtTerm);
      });
    }

    if (this.el.navTrack) {
      this.el.navTrack.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-wwt-cat]');
        if (!btn) return;
        self.setCategory(btn.dataset.wwtCat);
      });
    }

    // Card clicks and the empty-state reset are delegated from the directory,
    // so re-rendering never leaves stale listeners behind.
    if (this.el.directory) {
      this.el.directory.addEventListener('click', function (event) {
        var card = event.target.closest('[data-wwt-insight]');
        if (card) {
          self.openInsight(card.dataset.wwtInsight, true);
          return;
        }
        if (event.target.closest('[data-wwt-reset]')) self.resetAll();
      });
    }

    window.addEventListener('resize', function () {
      self.moveIndicator();
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        self.moveIndicator();
      });
    }
  };

  WhatWeTest.prototype.setSearch = function (term) {
    this.state.query = String(term || '').trim();
    if (this.state.query) this.state.category = ALL;
    if (this.el.search) {
      this.el.search.value = this.state.query;
      this.el.search.focus();
    }
    this.root.classList.toggle('is-searching', this.state.query.length > 0);
    this.render();
  };

  WhatWeTest.prototype.resetSearch = function () {
    this.state.query = '';
    if (this.el.search) {
      this.el.search.value = '';
      this.el.search.focus();
    }
    this.root.classList.remove('is-searching');
    this.render();
  };

  WhatWeTest.prototype.resetAll = function () {
    this.state.query = '';
    this.state.category = ALL;
    if (this.el.search) this.el.search.value = '';
    this.root.classList.remove('is-searching');
    this.render();
  };

  WhatWeTest.prototype.setCategory = function (id) {
    if (this.state.category === id && !this.state.query) return;
    this.state.category = id;
    /* Browsing a category exits search so the count and pills stay honest. */
    this.state.query = '';
    if (this.el.search) this.el.search.value = '';
    this.root.classList.remove('is-searching');
    this.render();

    // Keep the chosen pill in view inside the mobile scroller.
    var active = this.el.navTrack && this.el.navTrack.querySelector('[data-wwt-cat="' + id + '"]');
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  };

  /* ── filtering + rendering ─────────────────────────────────────────────── */

  WhatWeTest.prototype.filtered = function () {
    var query = this.state.query.toLowerCase();
    var category = this.state.category;
    var out = [];

    for (var i = 0; i < this.insights.length; i++) {
      var item = this.insights[i];
      if (category !== ALL && item.category !== category) continue;
      if (query && item._search.indexOf(query) === -1) continue;
      out.push(item);
    }

    if (query) {
      // Name matches first, then alphabetical, so "caffeine" leads with the
      // two caffeine insights rather than whatever mentions it in passing.
      out.sort(function (a, b) {
        var aName = a._sort.indexOf(query) === 0 ? 0 : a._sort.indexOf(query) > -1 ? 1 : 2;
        var bName = b._sort.indexOf(query) === 0 ? 0 : b._sort.indexOf(query) > -1 ? 1 : 2;
        if (aName !== bName) return aName - bName;
        return a._sort < b._sort ? -1 : a._sort > b._sort ? 1 : 0;
      });
    } else {
      out.sort(function (a, b) {
        return a._sort < b._sort ? -1 : a._sort > b._sort ? 1 : 0;
      });
    }

    return out;
  };

  WhatWeTest.prototype.render = function () {
    var self = this;
    var items = this.filtered();
    var query = this.state.query;
    var category = this.state.category;
    var grouped = category === ALL && !query;

    // Pills
    if (this.pills) {
      for (var i = 0; i < this.pills.length; i++) {
        this.pills[i].setAttribute(
          'aria-pressed',
          this.pills[i].dataset.wwtCat === category ? 'true' : 'false'
        );
      }
    }
    this.moveIndicator();

    // Count label
    if (this.el.count) {
      this.el.count.innerHTML = this.countLabel(items.length);
    }

    // Optional single-line category header
    if (this.el.catHeader) {
      var cat = this.catById[category];
      if (cat && !query) {
        this.el.catHeader.hidden = false;
        this.el.catTitle.textContent = cat.label;
        this.el.catDesc.textContent = cat.description || '';
      } else {
        this.el.catHeader.hidden = true;
      }
    }

    // Grid
    var html = '';
    if (grouped) {
      for (var c = 0; c < this.categories.length; c++) {
        var group = this.categories[c];
        var groupItems = items.filter(function (item) {
          return item.category === group.id;
        });
        if (!groupItems.length) continue;
        html +=
          '<section class="wwt__group">' +
          '<div class="wwt__group-head">' +
          '<span class="wwt__group-icon">' + icon(group.icon) + '</span>' +
          '<p class="wwt__group-title">' + escapeHtml(group.label) + '</p>' +
          '<span class="wwt__group-count">' + groupItems.length + '</span>' +
          '</div>' +
          this.gridHtml(groupItems, query) +
          '</section>';
      }
    } else if (items.length) {
      html = this.gridHtml(items, query);
    }

    this.el.results.innerHTML = html;
    this.el.empty.hidden = items.length > 0;

    // A very short dip in opacity so a filter change reads as a transition.
    var directory = this.el.directory;
    directory.classList.add('is-filtering');
    if (this._fade) clearTimeout(this._fade);
    this._fade = setTimeout(function () {
      directory.classList.remove('is-filtering');
      self._fade = null;
    }, 60);
  };

  WhatWeTest.prototype.gridHtml = function (items, query) {
    var html = '<ul class="wwt__grid" role="list">';

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      html +=
        '<li>' +
        '<button type="button" class="wwt__card" data-wwt-insight="' +
        escapeHtml(item.id) +
        '" aria-haspopup="dialog">' +
        '<span class="wwt__card-top">' +
        '<span class="wwt__card-cat">' +
        icon(item._catIcon) +
        '<span>' + escapeHtml(item._catLabel) + '</span>' +
        '</span>' +
        '<span class="wwt__card-arrow">' + ARROW_ICON + '</span>' +
        '</span>' +
        '<span class="wwt__card-name">' +
        highlight(escapeHtml(item.name), query) +
        '</span>' +
        '<span class="wwt__card-desc">' +
        highlight(escapeHtml(item.shortDescription), query) +
        '</span>' +
        '</button>' +
        '</li>';
    }

    return html + '</ul>';
  };

  WhatWeTest.prototype.countLabel = function (total) {
    var query = this.state.query;
    var category = this.state.category;
    var noun = total === 1 ? 'insight' : 'insights';

    if (query) {
      return (
        '<strong>' + total + '</strong> ' + (total === 1 ? 'result' : 'results') +
        ' for &ldquo;' + escapeHtml(query) + '&rdquo;'
      );
    }

    if (category !== ALL) {
      var cat = this.catById[category];
      return (
        '<strong>' + total + '</strong> ' + noun +
        (cat ? ' in ' + escapeHtml(cat.label) : '')
      );
    }

    if (this.allLabel) return '<strong>' + escapeHtml(this.allLabel) + '</strong> ' + noun;
    return '<strong>' + total + '</strong> ' + noun;
  };

  /* ── drawer ────────────────────────────────────────────────────────────── */

  WhatWeTest.prototype.setupDrawer = function () {
    var self = this;
    var drawer = this.el.drawer;
    if (!drawer) return;

    /* Reparent to <body>. This takes the fixed panel out of the section's
       stacking and overflow context, and out of #MainContent, where the
       site-wide type rules would otherwise resize its heading. */
    document.body.appendChild(drawer);

    this.drawer = {
      root: drawer,
      panel: drawer.querySelector('[data-wwt-panel]'),
      cat: drawer.querySelector('[data-wwt-drawer-cat]'),
      title: drawer.querySelector('[data-wwt-drawer-title]'),
      intro: drawer.querySelector('[data-wwt-drawer-intro]'),
      close: drawer.querySelector('[data-wwt-drawer-close]'),
      fields: {
        whatItIs: drawer.querySelector('[data-wwt-field="whatItIs"]'),
        whyItMatters: drawer.querySelector('[data-wwt-field="whyItMatters"]'),
        geneticInfluence: drawer.querySelector('[data-wwt-field="geneticInfluence"]'),
        howMyoformUsesIt: drawer.querySelector('[data-wwt-field="howMyoformUsesIt"]'),
      },
    };

    drawer.addEventListener('click', function (event) {
      if (event.target.closest('[data-wwt-drawer-close]') || event.target.closest('[data-wwt-backdrop]')) {
        self.closeDrawer(true);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!self.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        self.closeDrawer(true);
        return;
      }
      if (event.key === 'Tab') self.trapFocus(event);
    });

    window.addEventListener('popstate', function () {
      var id = (window.location.hash || '').replace('#', '');
      if (id && self.byId[id]) {
        self.openInsight(id, false);
      } else if (self.isOpen) {
        self.closeDrawer(false);
      }
    });
  };

  WhatWeTest.prototype.openFromHash = function () {
    var id = (window.location.hash || '').replace('#', '');
    if (id && this.byId[id]) this.openInsight(id, false);
  };

  WhatWeTest.prototype.openInsight = function (id, push) {
    var item = this.byId[id];
    var d = this.drawer;
    if (!item || !d) return;

    d.cat.innerHTML = icon(item._catIcon) + '<span>' + escapeHtml(item._catLabel) + '</span>';
    d.title.textContent = item.name;
    d.intro.textContent = item.shortDescription || '';

    Object.keys(d.fields).forEach(function (key) {
      var field = d.fields[key];
      if (!field) return;
      var value = item[key];
      field.hidden = !value;
      if (value) field.querySelector('[data-wwt-ftext]').textContent = value;
    });

    d.panel.scrollTop = 0;
    var scroll = d.panel.querySelector('[data-wwt-scroll]');
    if (scroll) scroll.scrollTop = 0;

    if (!this.isOpen) {
      this.lastFocus = document.activeElement;
      this.lockScroll(true);
      d.root.classList.add('is-open');
      this.isOpen = true;
    }

    d.root.setAttribute('aria-label', item.name);
    d.close.focus({ preventScroll: true });

    if (push && window.location.hash !== '#' + id) {
      history.pushState({ wwtInsight: id }, '', '#' + id);
    }
  };

  WhatWeTest.prototype.closeDrawer = function (updateHistory) {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.drawer.root.classList.remove('is-open');
    this.lockScroll(false);

    if (this.lastFocus && this.lastFocus.focus) {
      this.lastFocus.focus({ preventScroll: true });
    }
    this.lastFocus = null;

    if (!updateHistory) return;

    // Prefer stepping back so the browser's own back button stays in sync;
    // fall back to stripping the hash when we did not push an entry.
    if (history.state && history.state.wwtInsight) {
      history.back();
    } else if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  WhatWeTest.prototype.lockScroll = function (lock) {
    if (lock) {
      var gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) document.body.style.paddingRight = gap + 'px';
      document.body.classList.add('wwt-locked');
    } else {
      document.body.classList.remove('wwt-locked');
      document.body.style.paddingRight = '';
    }
  };

  WhatWeTest.prototype.trapFocus = function (event) {
    var focusable = this.drawer.panel.querySelectorAll(
      'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ── boot ──────────────────────────────────────────────────────────────── */

  function init() {
    var roots = document.querySelectorAll('[data-wwt-root]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].dataset.wwtInit) continue;
      roots[i].dataset.wwtInit = '1';
      new WhatWeTest(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', init);
})();
